import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";

const SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

function loadScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function UpgradeButton({ children, className }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  const start = async () => {
    trackEvent("upgrade_click", { plan: "premium", logged_in: Boolean(user) });
    if (!user) {
      window.location.href = "/login?next=/pricing";
      return;
    }
    setBusy(true);
    try {
      const ok = await loadScript();
      if (!ok) throw new Error("Could not load Razorpay");
      const { data: order } = await api.post("/billing/order", { plan: "premium" });
      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: order.name,
        description: order.description,
        prefill: order.prefill,
        theme: { color: "#0A0A0B" },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful · upgraded to Premium");
            trackEvent("purchase", {
              plan: "premium",
              currency: order.currency,
              value: (order.amount || 0) / 100,
              transaction_id: resp.razorpay_payment_id,
            });
            await refresh?.();
          } catch (e) {
            toast.error(formatApiError(e));
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed"));
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={start} disabled={busy} data-testid="upgrade-btn" className={className || "btn-brut-accent w-full"}>
      <CreditCard size={16}/> {busy ? "Loading…" : (children || "Upgrade to Premium")} <ArrowRight size={14}/>
    </button>
  );
}
